import { useCallback, useEffect, useRef, useState } from 'react';
import { startPlaybackSession, type PlaybackSession } from '../audio/playbackSession';
import { resumeAudio } from '../audio/noteSounds';
import { buildPlaybackSchedule } from '../utils/playbackSchedule';
import type { ChordAccompaniment, DrumPattern, Pattern } from '../types';
import type { MixVolumes } from '../utils/mixVolumes';

export function usePlayback(
  pattern: Pattern,
  bpm: number,
  soundEnabled: boolean,
  drumPattern: DrumPattern | null = null,
  chordAccompaniment: ChordAccompaniment | null = null,
  mixVolumes: MixVolumes,
) {
  const [playing, setPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeMeasureIndex, setActiveMeasureIndex] = useState<number | null>(null);
  const [activeDrumStep, setActiveDrumStep] = useState<number | null>(null);
  const sessionRef = useRef<PlaybackSession | null>(null);
  const playingRef = useRef(false);

  const stop = useCallback(() => {
    sessionRef.current?.stop();
    sessionRef.current = null;
    playingRef.current = false;
    setPlaying(false);
    setActiveIndex(null);
    setActiveMeasureIndex(null);
    setActiveDrumStep(null);
  }, []);

  const play = useCallback(async () => {
    stop();

    const schedule = buildPlaybackSchedule(pattern, bpm, drumPattern, chordAccompaniment);
    if (schedule.totalDurationSec <= 0) return;

    if (soundEnabled) await resumeAudio();

    playingRef.current = true;
    setPlaying(true);

    sessionRef.current = startPlaybackSession(
      schedule,
      {
        soundEnabled,
        onMelodyStep: (globalIndex, measureIndex) => {
          if (!playingRef.current) return;
          setActiveIndex(globalIndex);
          setActiveMeasureIndex(measureIndex);
        },
        onDrumStep: (drumStep) => {
          if (!playingRef.current) return;
          setActiveDrumStep(drumStep);
        },
        onEnd: () => {
          if (!playingRef.current) return;
          stop();
        },
      },
      mixVolumes,
    );
  }, [pattern, bpm, soundEnabled, drumPattern, chordAccompaniment, mixVolumes, stop]);

  useEffect(() => () => stop(), [stop]);

  return {
    playing,
    activeIndex,
    activeMeasureIndex,
    activeDrumStep,
    play,
    stop,
  };
}
